import { Clipboard } from "@capacitor/clipboard";
import { useCallback, useMemo, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router";

import ExpandableSection from "@components/animations/ExpandableSection";
import ActionIconButton from "@components/buttons/ActionIconButton";
import Button from "@components/buttons/Button";
import DateStamp from "@components/dates/DateStamp";
import TimeRange from "@components/dates/TimeRange";
import ExpertAvatar from "@components/ExpertAvatar";
import styles from "@components/expertSection/ExpertSection.module.css";
import ExpertRescheduleSession from "@components/expertSection/schedule/ExpertRescheduleSession";
import ConsultCancelForm from "@components/forms/ConsultCancelForm";
import ConsultMoveForm from "@components/forms/ConsultMoveForm";
import ConsultUpdateForm from "@components/forms/ConsultUpdateForm";
import NewJourneyForm from "@components/forms/NewJourneyForm";
import Tag from "@components/labels/Tag";
import Modal, { ModalElement } from "@components/Modal";
import Nbsp from "@components/Nbsp";
import AlertPopover from "@components/popovers/AlertPopover";
import IconBase from "@components/text/IconBase";
import TextWithIcon from "@components/text/TextWithIcon";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import InternalLink from "@components/typography/InternalLink";
import useMyExpert from "@hooks/useMyExpert";
import useOpener from "@hooks/useOpener";
import useSanitizeHtml from "@hooks/useSanitizeHtml";
import { ContactMethodEnum, GetJourneyWithExpertAndConsultsQuery } from "@typing/Generated";
import { languageNameForCode } from "@utils/languageUtils";
import { isRichTextEmpty } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";
import { consultEndsInFuture, consultStartsInFuture, convertTZ, timezoneAbbreviation } from "@utils/timeUtils";

type Props = {
  consult: GetJourneyWithExpertAndConsultsQuery["journey"]["consults"][0];
  journey: GetJourneyWithExpertAndConsultsQuery["journey"];
};

const ExpertSessionCard = ({ consult, journey }: Props) => {
  const history = useHistory();
  const intl = useIntl();
  const myExpert = useMyExpert();
  const extraInfoOpener = useOpener();
  const editConsultOpener = useOpener();
  const consultMoveOpener = useOpener();
  const newJourneyModalOpener = useOpener();
  const cancelConsultOpener = useOpener();
  const rescheduleOpener = useOpener();
  const newJourneyModalRef = useRef<ModalElement>(null);
  const sanitizedNotes = useSanitizeHtml(consult.notes, journey.id, consult);

  const differentTimezoneRoles = journey.clientRoles.filter(role => role.client.timezone !== myExpert?.timezone);

  const handleNewJourneyError = useCallback(() => {
    if (newJourneyModalRef.current) {
      newJourneyModalRef.current.scrollToTop();
    }
  }, [newJourneyModalRef]);

  const handleExpertCancelConsult = useCallback(() => {
    if (myExpert && !consultStartsInFuture(consult)) {
      cancelConsultOpener.open();
    } else if (consultStartsInFuture(consult)) {
      const url = journeyRoute({ journeyId: journey.id, linkAfterJourney: `/consults/${consult.id}/cancel`, myExpert });
      history.push(url);
    } else {
      // This means a client is attempting to cancel a consult that started in the past. That shouldn't
      // be possible because we hide the cancel link when the time passes.
    }
  }, [cancelConsultOpener, consult, history, journey.id, myExpert]);

  const handleCopyPhoneNumber = useCallback(() => {
    if (consult.phoneNumber?.number) {
      Clipboard.write({ string: consult.phoneNumber.number });
    }
  }, [consult.phoneNumber]);

  const hasPublishedReport = useMemo((): boolean => {
    if (journey.initialConsultReport.consult?.id === consult.id) {
      return journey.initialConsultReport.published;
    } else {
      return consult.consultReport?.published ?? false;
    }
  }, [
    consult.consultReport?.published,
    consult.id,
    journey.initialConsultReport.consult?.id,
    journey.initialConsultReport.published
  ]);

  return (
    <div className={styles.sessionCard} data-test="session-card">
      <div className={styles.sessionCardTop}>
        <div className={styles.expertAndTime}>
          <ExpertAvatar expert={consult.expert} />
          <div>
            <BodyCopy bold color="primary">
              <DateStamp value={consult.startTime} />
            </BodyCopy>
            <div>
              <BodyCopy color="primary">
                <TimeRange endTime={consult.endTime} startTime={consult.startTime} />
              </BodyCopy>
              {differentTimezoneRoles?.map(role => {
                if (role.client.timezone) {
                  return (
                    <div className={styles.timezone} key={role.id}>
                      <BodyCopy color="gray-500">
                        {convertTZ(consult.startTime, role.client.timezone)}
                        <Nbsp />
                        {timezoneAbbreviation(role.client.timezone)}
                        <Nbsp />
                        <FormattedMessage id="dictionary.punctuation.dash" />
                        <Nbsp />
                        {role.client.firstName}
                      </BodyCopy>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </div>
        </div>
        <div className={styles.expertContact}>
          {consult.canceled ? (
            <div>
              <BodyCopy color="danger-500">
                <FormattedMessage id="pages.expertSection.canceled" />
                {consult.cancelationReason && (
                  <>
                    <Nbsp />
                    <FormattedMessage id="dictionary.punctuation.dash" />
                    <Nbsp /> <FormattedMessage id={`models.consult.${consult.cancelationReason}`} />
                  </>
                )}
              </BodyCopy>
            </div>
          ) : consultStartsInFuture(consult) ? (
            <div className={styles.consultItems}>
              <BodyCopy color="success-500">
                <FormattedMessage id="pages.expertSection.scheduled" />
              </BodyCopy>
              <div>
                {consult.joinUrl && (
                  <Button href={consult.joinUrl} rel="noopener noreferrer" target="_blank">
                    <IconBase
                      icon={consult.contactMethod === ContactMethodEnum.PHONE ? "phone" : "video"}
                      slot="start"
                    />
                    <FormattedMessage id="pages.expertSection.join" />
                  </Button>
                )}
                {consult.contactMethod === ContactMethodEnum.PHONE && (
                  <AlertPopover textKey="dictionary.copied">
                    <div className={styles.contactMethodNumber} onClick={handleCopyPhoneNumber}>
                      <BodyCopy color="primary">{consult.phoneNumber?.formattedNumber}</BodyCopy>
                    </div>
                  </AlertPopover>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.consultItems}>
              <BodyCopy color="primary">
                <FormattedMessage id={`models.duration.${consult.durationType}`} />
              </BodyCopy>
              {hasPublishedReport ? (
                <Tag color="success">
                  <FormattedMessage id="models.consult.finalized" />
                </Tag>
              ) : (
                <InternalLink
                  stylized={false}
                  to={journeyRoute({
                    journeyId: consult.journey.id,
                    linkAfterJourney: `/care-profile?docSection=sessions`,
                    myExpert
                  })}
                >
                  <Tag color="danger">
                    <FormattedMessage id="models.consult.notFinalized" />
                  </Tag>
                </InternalLink>
              )}
            </div>
          )}
        </div>
        <div className={styles.consultItems + " align-right"}>
          <div className={styles.clientInvites}>
            {consult.invites.map(invite => (
              <div key={invite.id}>
                <BodyCopy color="primary">{invite.client?.fullName ?? invite.careTeamInvite?.email}</BodyCopy>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.consultItems}>
          <div className={styles.iconSpacingContainer}>
            {(!isRichTextEmpty(consult.notes ?? "") || consult.languageCode || consult.cancelationNote) && (
              <ActionIconButton
                ariaLabelKey="accessibility.ariaLabels.toggle"
                color="secondary"
                hoverContent={intl.formatMessage({ id: "pages.expertSection.extraInfo" })}
                icon="circleExclamation"
                onClick={extraInfoOpener.toggle}
              />
            )}
          </div>
          <div className={styles.iconSpacingContainer}>
            {journey.initialConsultReport.consult?.id !== consult.id && (
              <ActionIconButton
                ariaLabelKey="models.consult.moveToAnotherJourney"
                hoverContent={intl.formatMessage({ id: "models.consult.moveToAnotherJourney" })}
                icon="peopleArrows"
                onClick={consultMoveOpener.toggle}
              />
            )}
          </div>
          <div className={styles.iconSpacingContainer}>
            {consultEndsInFuture(consult) && (
              <ActionIconButton
                ariaLabelKey="pages.expertSection.reschedule"
                hoverContent={intl.formatMessage({ id: "pages.expertSection.reschedule" })}
                icon="calendar"
                onClick={rescheduleOpener.toggle}
              />
            )}
          </div>
          <div className={styles.iconSpacingContainer}>
            {!consult.canceled && (
              <ActionIconButton
                ariaLabelKey="pages.consults.reschedule.updated.cancel"
                hoverContent={intl.formatMessage({ id: "pages.consults.reschedule.updated.cancel" })}
                icon="calendarXmark"
                onClick={handleExpertCancelConsult}
              />
            )}
          </div>
          <div className={styles.iconSpacingContainer}>
            <ActionIconButton
              ariaLabelKey="accessibility.ariaLabels.edit"
              hoverContent={intl.formatMessage({ id: "dictionary.edit" })}
              icon="edit"
              onClick={editConsultOpener.toggle}
            />
          </div>
        </div>
      </div>
      <ExpandableSection isOpen={extraInfoOpener.isOpen} key="extra-info-session">
        <div className={styles.sessionCardBottom}>
          {!isRichTextEmpty(consult.notes ?? "") && (
            <>
              <BodyCopy bold color="gray">
                <FormattedMessage id="forms.consult.notesLabel" />
              </BodyCopy>
              <BodyCopy color="gray-500">{sanitizedNotes}</BodyCopy>
            </>
          )}
          {consult.languageCode && (
            <>
              <div className={styles.infoSection}>
                <BodyCopy bold color="gray">
                  <FormattedMessage id="forms.consult.languageCodeLabel" />
                </BodyCopy>
              </div>
              <BodyCopy color="gray-500">
                <TextWithIcon icon="commentAltPlus" iconFirst text={languageNameForCode(consult.languageCode)} />
              </BodyCopy>
            </>
          )}
          {consult.cancelationNote && (
            <>
              <div className={styles.infoSection}>
                <BodyCopy bold color="gray">
                  <FormattedMessage id="forms.consultCancel.noteLabel" />
                </BodyCopy>
              </div>
              <BodyCopy color="gray-500">
                <BodyCopy color="gray-500">{consult.cancelationNote}</BodyCopy>
              </BodyCopy>
            </>
          )}
        </div>
      </ExpandableSection>
      <ExpandableSection isOpen={rescheduleOpener.isOpen} key="reschedule-session">
        <div className={styles.expertReschedule}>
          <ExpertRescheduleSession consult={consult} journey={journey} />
        </div>
      </ExpandableSection>
      <Modal opener={editConsultOpener} title={intl.formatMessage({ id: "models.consult.editConsult" })}>
        {() => (
          <>
            <div className={styles.intro}>
              <BodyCopy bold>
                <FormattedMessage id="models.consult.dateAndTime" />
              </BodyCopy>
            </div>
            <div>
              <BodyCopy>
                <div>
                  <DateStamp value={consult.startTime} />
                </div>
                <div>
                  <TimeRange endTime={consult.endTime} startTime={consult.startTime} />
                </div>
              </BodyCopy>
            </div>

            <ConsultUpdateForm
              consult={consult}
              journey={journey}
              onCancel={editConsultOpener.close}
              opener={editConsultOpener}
            />
          </>
        )}
      </Modal>
      <Modal opener={consultMoveOpener} title={intl.formatMessage({ id: "models.consult.moveToAnotherJourney" })}>
        {() => (
          <>
            <div>
              <BodyCopy bold>
                <FormattedMessage id="models.consult.consultFor" />
              </BodyCopy>
            </div>
            <div className={styles.consultFor}>
              {consult.invites
                .filter(invite => invite.client)
                .map((invitee, index) => (
                  <BodyCopy key={invitee.client?.id}>
                    {invitee.client?.fullName}
                    {index < consult.invites.length - 1 && (
                      <>
                        <FormattedMessage id="dictionary.punctuation.comma" />
                        <Nbsp />
                      </>
                    )}
                  </BodyCopy>
                ))}
            </div>
            <div className={styles.reschedule}>
              <div>
                <FormattedMessage id="models.consult.newJourney" />
              </div>
              <ButtonLink onClick={newJourneyModalOpener.open}>
                <FormattedMessage id="models.consult.createNewJourney" />
              </ButtonLink>
            </div>
            {journey.owner.id && (
              <ConsultMoveForm
                clientId={journey.owner.id}
                consult={consult}
                journeyId={journey.id}
                onCancel={consultMoveOpener.close}
                onSuccess={consultMoveOpener.close}
              />
            )}
          </>
        )}
      </Modal>
      <Modal
        opener={newJourneyModalOpener}
        ref={newJourneyModalRef}
        title={intl.formatMessage({ id: "pages.expert.journeys.newJourneyModal.title" })}
      >
        {({ modalDisplayed }) => {
          if (journey.owner.id) {
            return (
              <NewJourneyForm
                autofocus={modalDisplayed}
                clientId={journey.owner.id}
                onCancel={newJourneyModalOpener.close}
                onError={handleNewJourneyError}
                onSuccess={newJourneyModalOpener.close}
              />
            );
          }
          return null;
        }}
      </Modal>
      <Modal opener={cancelConsultOpener} title={intl.formatMessage({ id: "models.consult.whyCancel" })}>
        {() => (
          <ConsultCancelForm
            consultId={consult.id}
            onCancel={cancelConsultOpener.close}
            onSuccess={cancelConsultOpener.close}
          />
        )}
      </Modal>
    </div>
  );
};

export default ExpertSessionCard;
